import { Button, Combobox, Input, Item, ItemActions, ItemHeader, ItemTitle, Textarea } from '@ibsys/kit';
import { FC } from 'react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AttributeType,
  DISPLAY_FORMAT_TYPES,
  DisplayFormatType,
  isDisplayFormatAllowed,
  isListAllowed,
} from '@/entities/attributes';
import { DESTRUCTIVE_BUTTON_CLASS } from '@/shared/lib';
import { useFormCodeGeneration } from '@/shared/hooks';
import { AiInput } from '@/shared/ui';
import { useGetAttributeKinds, useGetAttributeTypes, useGetDisplayFormats } from '../../../utils';
import { AttributeFormValues } from '../validation';
import { ListField } from '../ListField';
import { RuleField } from '../RuleField';

type Props = {
  index: number;
  disabled?: boolean;
  /** Структура уже создана: отсюда сохраняется только подсказка, остальное — в собственной карточке атрибута */
  isSaved?: boolean;
  onRemove: () => void;
};

export const StructureAttributeItem: FC<Props> = ({ index, disabled, isSaved, onRemove }) => {
  const { t } = useTranslation(['attributes', 'common']);
  const { control, getValues, setValue, watch } = useFormContext<AttributeFormValues>();
  const { options } = useGetAttributeTypes();
  const { options: kindOptions } = useGetAttributeKinds();

  const attribute = watch(`structure.attributes.${index}`);
  const { options: displayFormatOptions } = useGetDisplayFormats(attribute?.dataType as AttributeType | null);

  const typeOptions = options.map((o) => ({ label: o.label!, value: o.value }));
  const isLocked = disabled || isSaved;

  // Код создаваемого атрибута задаётся так же, как в карточке отдельного атрибута: по названию
  const { isCodeLocked, isGeneratingCode, handleNameBlur } = useFormCodeGeneration({
    getName: () => getValues(`structure.attributes.${index}.name`) ?? '',
    getCode: () => getValues(`structure.attributes.${index}.code`) ?? '',
    setCode: (code) => setValue(`structure.attributes.${index}.code`, code),
    entityType: 'attribute',
    isEdit: !attribute?.isNew,
  });

  // Формат отображения есть только у принимающих его типов. Задаётся он, как вид и правило, лишь у создаваемого
  // атрибута; у добавленного из существующих и у вложенного сохранённой структуры виден закрытым
  const isDisplayFormatSupported =
    !!attribute?.dataType && DISPLAY_FORMAT_TYPES.includes(attribute.dataType as DisplayFormatType);

  // Формат отображения принадлежит типу: несочетаемая с новым типом пара не сохраняется
  useEffect(() => {
    if (!isDisplayFormatAllowed(attribute?.dataType, attribute?.displayFormat)) {
      setValue(`structure.attributes.${index}.displayFormat`, null);
    }
  }, [attribute?.dataType, attribute?.displayFormat, index, setValue]);

  // Выбор из справочника списком не бывает — так же, как в карточке отдельного атрибута
  const isListApplicable = isListAllowed(attribute?.dataType);
  useEffect(() => {
    if (!isListApplicable && attribute?.isList) {
      setValue(`structure.attributes.${index}.isList`, false);
    }
  }, [isListApplicable, attribute?.isList, index, setValue]);

  return (
    <Item variant="outline" size="sm" className="w-full">
      <ItemHeader>
        <ItemTitle>{attribute?.isNew ? t('attributes:newAttribute') : attribute?.name}</ItemTitle>
        <ItemActions>
          {!isLocked && (
            <Button
              variant="ghost"
              color="destructive"
              className={DESTRUCTIVE_BUTTON_CLASS}
              size="sm"
              onClick={onRemove}
            >
              {t('common:delete')}
            </Button>
          )}
        </ItemActions>
      </ItemHeader>
      <div className="flex flex-col gap-3 p-3 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            control={control}
            name={`structure.attributes.${index}.code`}
            label={t('attributes:code')}
            required={attribute?.isNew}
            disabled={disabled || !attribute?.isNew || isCodeLocked}
            placeholder={isCodeLocked ? t('common:nameIsTheFirst') : undefined}
          />
          <AiInput
            control={control}
            name={`structure.attributes.${index}.name`}
            label={t('attributes:name')}
            required={!isSaved}
            isProcessing={isGeneratingCode}
            disabled={isLocked}
            onBlur={handleNameBlur}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:items-baseline-last">
          <Combobox
            control={control}
            name={`structure.attributes.${index}.dataType`}
            label={t('attributes:dataType')}
            required={attribute?.isNew}
            disabled={disabled || !attribute?.isNew}
            options={typeOptions}
          />
          <ListField
            name={`structure.attributes.${index}.isList`}
            disabled={disabled || !attribute?.isNew || !isListApplicable}
          />
        </div>
        {isDisplayFormatSupported && (
          <Combobox
            control={control}
            name={`structure.attributes.${index}.displayFormat`}
            label={t('attributes:displayFormat.title')}
            disabled={disabled || !attribute?.isNew}
            options={displayFormatOptions}
          />
        )}
        <Combobox
          control={control}
          name={`structure.attributes.${index}.type`}
          label={t('attributes:attributeKind.title')}
          disabled={disabled || !attribute?.isNew}
          options={kindOptions}
        />
        <RuleField
          name={`structure.attributes.${index}.rule`}
          rows={2}
          disabled={disabled || !attribute?.isNew}
          inputClassName="max-h-[150px]"
        />
        <Textarea
          control={control}
          name={`structure.attributes.${index}.description`}
          label={t('attributes:description')}
          rows={2}
          disabled={isLocked}
        />
        <Textarea
          control={control}
          name={`structure.attributes.${index}.tooltip`}
          label={t('attributes:tooltip')}
          rows={2}
          disabled={disabled}
        />
      </div>
    </Item>
  );
};
